import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { conversationService } from '../services/conversationService';
import { ConversationFeedback } from '../../../types';
import { useSocketEvent, SOCKET_EVENTS } from '../../../lib/websocket';

/**
 * CSAT feedback rows for a single conversation, newest resolution first.
 *
 * Subscribes to feedback:recorded so the timeline updates the moment a
 * customer taps a star, without a refetch. The pending row already in the
 * list (sent by the prompt job) is mutated in place from pending → responded.
 */
export function useFeedback(conversationId: string | null) {
  const queryClient = useQueryClient();

  const { data: feedback = [], isLoading } = useQuery<ConversationFeedback[]>({
    queryKey: ['feedback', conversationId],
    queryFn: () => conversationService.getFeedback(conversationId!),
    enabled: !!conversationId,
  });

  useSocketEvent(SOCKET_EVENTS.FEEDBACK_RECORDED, (data) => {
    if (data.conversationId !== conversationId) return;
    queryClient.setQueryData<ConversationFeedback[]>(
      ['feedback', conversationId],
      (prev) => {
        if (!prev) return prev;
        // Mutate the most recent pending row in place — there's at most one
        // per resolution event and the new score must match it (the prompt
        // job won't have prompted again before responded_at is set).
        let patched = false;
        const next = prev.map((row) => {
          if (!patched && row.status === 'pending') {
            patched = true;
            return {
              ...row,
              status: 'responded' as const,
              score: data.score,
              respondedAt: new Date(data.respondedAt),
            };
          }
          return row;
        });
        return next;
      },
    );
  });

  // Derived: the most recent rating, if any. Used by the "Rated X/5" pill.
  const latestRated = feedback.find((row) => row.status === 'responded');

  return {
    feedback,
    latestRated,
    isLoading,
  };
}

/**
 * Toggle the `disputed` flag on a single feedback row.
 *
 * Optimistically updates every feedback query cache so the timeline + header
 * pill react immediately. Re-fetches the list for the conversation that owns
 * the row on success to pick up any backend-side changes.
 */
export function useDisputeFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ feedbackId, disputed }: { feedbackId: string; disputed: boolean }) =>
      conversationService.disputeFeedback(feedbackId, disputed),
    onMutate: async ({ feedbackId, disputed }) => {
      // Optimistic update — patch every cached feedback list that contains this row.
      const snapshots = new Map<string, ConversationFeedback[] | undefined>();
      const caches = queryClient.getQueriesData<ConversationFeedback[]>({ queryKey: ['feedback'] });
      for (const [key, value] of caches) {
        snapshots.set(JSON.stringify(key), value);
        if (!value) continue;
        const patched = value.map((row) =>
          row.id === feedbackId ? { ...row, disputed } : row
        );
        queryClient.setQueryData(key, patched);
      }
      return { snapshots };
    },
    onError: (error: any, _vars, context) => {
      if (context?.snapshots) {
        for (const [key, value] of context.snapshots.entries()) {
          queryClient.setQueryData(JSON.parse(key), value);
        }
      }
      const message = error.response?.data?.detail || 'Failed to update feedback';
      toast.error(message);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['feedback', data.conversationId] });
      // Analytics summary changes when disputes flip, so refresh it too.
      queryClient.invalidateQueries({ queryKey: ['analytics-feedback-summary'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-staff-performance'] });
    },
  });
}
